'use client';

import { forwardRef } from 'react';
import { cn } from '../../../../lib/utils';
import { ArrowDropDown } from '@crabs-ai/web-icons';
import { InspectorControlsStyles as S } from '../inspector-controls-styles';

export interface SelectFieldTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 레이블 표시 여부 */
  isShowLabel?: boolean;
  /** 레이블 텍스트 */
  label?: string;
  /** 표시할 값 */
  value?: string;
  /** 값이 없을 때 표시할 텍스트 */
  placeholder?: string;
  /** 좌측 노드 (아이콘 등) */
  leadingNode?: React.ReactNode;
  /** 좌측 노드 표시 여부 */
  isShowLeadingNode?: boolean;
  /** Chevron(ArrowDropDown) 표시 여부 (기본: true) */
  showChevron?: boolean;
  /** Chevron 대신 표시할 커스텀 trailing 노드 (예: LoadingSpinner) */
  trailingNode?: React.ReactNode;
  /** Mixed value 상태 (여러 아이템 선택 시) */
  hasMixedValue?: boolean;
  /** Mixed value일 때 표시할 라벨 */
  mixedLabel?: string;
  /** 값 텍스트에 적용할 인라인 스타일 (예: 폰트 프리뷰) */
  valueStyle?: React.CSSProperties;
  /** 접근성 라벨 */
  accessibilityLabel?: string;
}

const SelectFieldTrigger = forwardRef<
  HTMLButtonElement,
  SelectFieldTriggerProps
>(
  (
    {
      isShowLabel = false,
      label,
      value,
      placeholder,
      leadingNode,
      isShowLeadingNode = true,
      showChevron = true,
      trailingNode,
      hasMixedValue = false,
      mixedLabel = 'Mixed',
      disabled,
      valueStyle,
      accessibilityLabel,
      className,
      ...rest
    },
    ref,
  ) => {
    const displayValue = hasMixedValue ? mixedLabel : value;
    const triggerButton = (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-label={accessibilityLabel}
        className={cn(
          S.container,
          'group w-full cursor-pointer text-left',
          'hover:not-disabled:not-[data-state=open]:bg-comp-select-trigger-bg-hover',
          'active:not-disabled:not-[data-state=open]:bg-comp-select-trigger-bg-active',
          'data-[state=open]:border data-[state=open]:border-comp-select-trigger-border-open data-[state=open]:bg-comp-select-trigger-bg-open',
          'transition-colors duration-200 ease-out',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
        {...rest}
      >
        {isShowLeadingNode && leadingNode && (
          <div
            className={cn(
              S.leadingNode.base,
              S.leadingNode.icon,
              disabled && S.leadingNode.iconDisabled,
            )}
          >
            {leadingNode}
          </div>
        )}

        <span
          className={cn(
            S.value.base,
            'truncate',
            displayValue ? S.value.filled : S.value.placeholder,
            disabled && S.value.disabled,
          )}
          style={valueStyle}
        >
          {displayValue || placeholder}
        </span>

        {trailingNode ? (
          <span className="flex flex-shrink-0 items-center">
            {trailingNode}
          </span>
        ) : (
          showChevron && (
            <span className="flex flex-shrink-0 items-center">
              <ArrowDropDown
                className={cn(
                  S.chevron.base,
                  'group-data-[state=open]:rotate-180',
                  disabled && S.chevron.disabled,
                )}
              />
            </span>
          )
        )}
      </button>
    );

    return isShowLabel && label ? (
      <div
        data-custom-state={disabled ? 'disabled' : 'default'}
        className="group relative flex flex-col gap-5xs"
      >
        <div className={S.label.wrapper}>
          <p
            className={cn(
              S.label.text,
              'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
            )}
          >
            {label}
          </p>
        </div>
        {triggerButton}
      </div>
    ) : (
      triggerButton
    );
  },
);

SelectFieldTrigger.displayName = 'SelectFieldTrigger';

export default SelectFieldTrigger;
